import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChefHat, Package, Printer, BarChart3, Download, Calendar,
  RefreshCw, LogOut, Settings,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import { format, subDays } from "date-fns";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const KitchenDashboard = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [salesData, setSalesData] = useState(null);
  const [salesRange, setSalesRange] = useState("today");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [showSales, setShowSales] = useState(false);
  const [updatingPayment, setUpdatingPayment] = useState(null);

  const getToken = useCallback(() => localStorage.getItem("admin_token"), []);

  const loadOrders = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/orders`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      setOrders(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem("admin_token");
        navigate("/admin-vow");
      }
    } finally {
      setLoading(false);
    }
  }, [getToken, navigate]);

  const loadSalesData = useCallback(async (range) => {
    try {
      const today = new Date();
      let params = {};
      if (range === "today") {
        const d = format(today, "yyyy-MM-dd");
        params = { start_date: d, end_date: d };
      } else if (range === "7days") {
        params = {
          start_date: format(subDays(today, 6), "yyyy-MM-dd"),
          end_date: format(today, "yyyy-MM-dd"),
        };
      } else if (range === "custom" && customStart && customEnd) {
        params = { start_date: customStart, end_date: customEnd };
      } else {
        return;
      }
      const qs = new URLSearchParams(params).toString();
      const response = await axios.get(`${API}/orders/eod-summary?${qs}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      setSalesData(response.data);
    } catch (error) {
      console.error("Error loading sales data:", error);
    }
  }, [getToken, customStart, customEnd]);

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    if (!token) { navigate("/admin-vow"); return; }
    loadOrders();
    loadSalesData("today");
    const interval = setInterval(() => { loadOrders(); }, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (showSales) loadSalesData(salesRange);
  }, [salesRange, showSales, loadSalesData]);

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      await axios.put(`${API}/orders/status`,
        { order_id: orderId, status: newStatus },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      toast.success("Status updated");
      loadOrders();
      if (showSales) loadSalesData(salesRange);
    } catch { toast.error("Failed to update status"); }
  };

  const updatePaymentMode = async (orderId, newMode) => {
    setUpdatingPayment(orderId);
    try {
      await axios.put(`${API}/orders/${orderId}/payment`,
        { actual_payment_mode: newMode },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      toast.success("Payment mode updated");
      loadOrders();
      if (showSales) loadSalesData(salesRange);
    } catch { toast.error("Failed to update payment"); }
    finally { setUpdatingPayment(null); }
  };

  const confirmRoom = async (orderId) => {
    try {
      await axios.put(`${API}/orders/${orderId}/confirm-room`, {},
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      toast.success("Room confirmed");
      loadOrders();
    } catch { toast.error("Failed to confirm room"); }
  };

  const handlePrintOrder = (order) => {
    const actualPay = order.actual_payment_mode || order.payment_mode;
    const win = window.open("", "_blank", "width=400,height=600");
    win.document.write(`<html><head><title>Order ${order.order_id}</title>
      <style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:Arial,sans-serif;padding:16px;color:#000;font-size:13px}.header{text-align:center;border-bottom:2px solid #000;padding-bottom:10px;margin-bottom:12px}.header h1{font-size:18px;margin-bottom:2px}.header p{font-size:11px;color:#444}.room-block{text-align:center;margin:12px 0}.room-block .room{font-size:36px;font-weight:bold}.room-block .label{font-size:11px;color:#666;text-transform:uppercase;letter-spacing:1px}.info-row{display:flex;justify-content:space-between;padding:3px 0}.info-row .label{color:#666}.info-row .value{font-weight:600}.divider{border-top:1px dashed #999;margin:10px 0}.items-table{width:100%;border-collapse:collapse;margin:8px 0}.items-table th{text-align:left;font-size:11px;color:#666;padding:4px 0;border-bottom:1px solid #ccc;text-transform:uppercase}.items-table td{padding:5px 0}.items-table .qty{text-align:center;width:30px}.items-table .price{text-align:right}.total-row{display:flex;justify-content:space-between;font-size:16px;font-weight:bold;padding:8px 0;border-top:2px solid #000;margin-top:4px}.footer{text-align:center;margin-top:16px;font-size:11px;color:#666}@media print{body{padding:0}}</style></head><body>
      <div class="header"><h1>${order.brand_name}</h1><p>Hospital Food Service</p></div>
      <div class="room-block"><div class="label">Room Number</div><div class="room">${order.room_number}</div></div>
      <div class="divider"></div>
      <div class="info-row"><span class="label">Order ID</span><span class="value">${order.order_id}</span></div>
      <div class="info-row"><span class="label">Date</span><span class="value">${format(new Date(order.created_at), "dd MMM yyyy, hh:mm a")}</span></div>
      <div class="info-row"><span class="label">Name</span><span class="value">${order.patient_name || "N/A"}</span></div>
      <div class="info-row"><span class="label">Phone</span><span class="value">${order.phone_number || "N/A"}</span></div>
      <div class="info-row"><span class="label">Payment</span><span class="value" style="text-transform:capitalize">${actualPay}</span></div>
      ${order.actual_payment_mode && order.actual_payment_mode !== order.payment_mode ? `<div class="info-row"><span class="label">Original</span><span class="value" style="text-transform:capitalize;color:#999">${order.payment_mode}</span></div>` : ""}
      <div class="info-row"><span class="label">Status</span><span class="value" style="text-transform:capitalize">${order.status}</span></div>
      <div class="divider"></div>
      <table class="items-table"><thead><tr><th>Item</th><th class="qty">Qty</th><th class="price">Amount</th></tr></thead><tbody>
      ${order.items.map(i => `<tr><td>${i.name}</td><td class="qty">${i.quantity}</td><td class="price">₹${(i.price*i.quantity).toFixed(2)}</td></tr>`).join("")}
      </tbody></table>
      <div class="total-row"><span>Total</span><span>₹${order.total_amount.toFixed(2)}</span></div>
      ${order.special_instructions ? `<div class="divider"></div><p style="font-size:11px;color:#666">Notes: ${order.special_instructions}</p>` : ""}
      <div class="footer"><p>Estimated delivery: 45-60 minutes</p></div>
      </body></html>`);
    win.document.close(); win.focus(); win.print(); win.close();
  };

  const downloadSalesPDF = () => {
    if (!salesData) return;
    const doc = new jsPDF();
    const pageW = doc.internal.pageSize.getWidth();

    doc.setFontSize(18);
    doc.text("VOW Hospital Food Service", pageW / 2, 18, { align: "center" });
    doc.setFontSize(11);
    doc.setTextColor(100);
    doc.text("Sales Report", pageW / 2, 25, { align: "center" });
    doc.text(`${salesData.start_date} to ${salesData.end_date}`, pageW / 2, 31, { align: "center" });

    doc.setDrawColor(200);
    doc.line(14, 35, pageW - 14, 35);

    doc.setFontSize(12);
    doc.setTextColor(0);
    let y = 44;
    doc.text("Summary", 14, y);
    y += 8;

    const summaryData = [
      ["Total Orders", String(salesData.order_count)],
      ["Total Sales", `Rs. ${salesData.total_sales.toFixed(2)}`],
      ["Cash", `Rs. ${salesData.cash_total.toFixed(2)}`],
      ["UPI", `Rs. ${salesData.upi_total.toFixed(2)}`],
      ["Card", `Rs. ${salesData.card_total.toFixed(2)}`],
    ];

    autoTable(doc, {
      startY: y,
      head: [["Metric", "Value"]],
      body: summaryData,
      theme: "grid",
      headStyles: { fillColor: [75, 95, 55], textColor: 255, fontStyle: "bold" },
      styles: { fontSize: 10 },
      columnStyles: { 0: { fontStyle: "bold" }, 1: { halign: "right" } },
      margin: { left: 14, right: 14 },
    });

    if (salesData.orders && salesData.orders.length > 0) {
      const orderRows = salesData.orders.map(o => [
        o.order_id,
        format(new Date(o.created_at), "dd MMM, hh:mm a"),
        o.room_number,
        o.patient_name || "N/A",
        o.items.map(i => `${i.name} x${i.quantity}`).join(", "),
        (o.actual_payment_mode || o.payment_mode || "").toUpperCase(),
        o.status.charAt(0).toUpperCase() + o.status.slice(1),
        `Rs. ${o.total_amount.toFixed(2)}`,
      ]);

      autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 12,
        head: [["Order ID", "Date", "Room", "Name", "Items", "Payment", "Status", "Amount"]],
        body: orderRows,
        theme: "grid",
        headStyles: { fillColor: [75, 95, 55], textColor: 255, fontStyle: "bold", fontSize: 8 },
        styles: { fontSize: 7.5, cellPadding: 2 },
        columnStyles: {
          0: { cellWidth: 22 },
          4: { cellWidth: 42 },
          7: { halign: "right" },
        },
        margin: { left: 14, right: 14 },
      });
    }

    const fileName = `VOW_Sales_${salesData.start_date}_to_${salesData.end_date}.pdf`;
    doc.save(fileName);
    toast.success("PDF downloaded");
  };

  const statusColors = {
    received: "bg-blue-100 text-blue-700 border-blue-200",
    preparing: "bg-yellow-100 text-yellow-700 border-yellow-200",
    delivered: "bg-green-100 text-green-700 border-green-200",
  };

  const filteredOrders = (filter === "all"
    ? orders : orders.filter(o => o.status === filter)
  ).sort((a, b) => {
    // Unconfirmed rooms sort to top
    const aUnconf = a.room_confirmed === false ? 0 : 1;
    const bUnconf = b.room_confirmed === false ? 0 : 1;
    return aUnconf - bUnconf;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="sticky top-0 z-10 backdrop-blur-md bg-background/90 border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-primary/10 p-2.5 rounded-xl">
                <ChefHat className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-foreground">Kitchen Dashboard</h1>
                <p className="text-sm text-muted-foreground">Manage orders & sales</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="outline" size="sm" onClick={loadOrders} className="gap-2" data-testid="refresh-orders">
                <RefreshCw className="h-4 w-4" /> Refresh
              </Button>
              <Button variant="outline" size="sm" onClick={() => navigate("/admin-vow/menu")} className="gap-2" data-testid="menu-management-link">
                <Settings className="h-4 w-4" /> Menu
              </Button>
              <Button variant="outline" size="sm" onClick={() => { localStorage.removeItem("admin_token"); navigate("/admin-vow"); }} className="gap-2" data-testid="logout-button">
                <LogOut className="h-4 w-4" /> Logout
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6 space-y-6">
        {/* Sales Summary Section */}
        <div className="bg-card rounded-xl border border-border/50 overflow-hidden">
          <button
            onClick={() => setShowSales(!showSales)}
            className="w-full flex items-center justify-between px-6 py-4 hover:bg-muted/30 transition-colors"
            data-testid="eod-summary-toggle"
          >
            <div className="flex items-center gap-3">
              <BarChart3 className="h-5 w-5 text-primary" />
              <span className="font-semibold text-foreground">Sales Summary</span>
            </div>
            <span className="text-sm text-muted-foreground">{showSales ? "Hide" : "Show"}</span>
          </button>

          {showSales && (
            <div className="px-6 pb-6 space-y-4" data-testid="eod-summary-panel">
              {/* Range Tabs */}
              <div className="flex flex-wrap items-center gap-3">
                {[
                  { key: "today", label: "Today" },
                  { key: "7days", label: "Last 7 Days" },
                  { key: "custom", label: "Custom Range" },
                ].map(tab => (
                  <Button key={tab.key} size="sm"
                    variant={salesRange === tab.key ? "default" : "outline"}
                    onClick={() => setSalesRange(tab.key)}
                    data-testid={`sales-range-${tab.key}`}
                  >
                    {tab.label}
                  </Button>
                ))}

                {salesRange === "custom" && (
                  <div className="flex items-center gap-2 ml-2">
                    <Input type="date" value={customStart}
                      onChange={e => setCustomStart(e.target.value)}
                      className="w-40 h-9" data-testid="custom-start-date" />
                    <span className="text-muted-foreground text-sm">to</span>
                    <Input type="date" value={customEnd}
                      onChange={e => setCustomEnd(e.target.value)}
                      className="w-40 h-9" data-testid="custom-end-date" />
                    <Button size="sm" variant="outline"
                      onClick={() => loadSalesData("custom")}
                      disabled={!customStart || !customEnd}
                      data-testid="apply-custom-range"
                    >
                      Apply
                    </Button>
                  </div>
                )}

                {salesData && (
                  <Button size="sm" variant="outline" onClick={downloadSalesPDF}
                    className="ml-auto gap-2" data-testid="download-pdf-button">
                    <Download className="h-4 w-4" /> Download PDF
                  </Button>
                )}
              </div>

              {/* Sales Cards */}
              {salesData && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div className="bg-primary/5 rounded-xl p-4 col-span-2 md:col-span-1">
                    <p className="text-xs text-muted-foreground mb-1">Orders</p>
                    <p className="text-2xl font-bold text-primary" data-testid="eod-order-count">{salesData.order_count}</p>
                  </div>
                  <div className="bg-primary/5 rounded-xl p-4">
                    <p className="text-xs text-muted-foreground mb-1">Total Sales</p>
                    <p className="text-2xl font-bold text-primary" data-testid="eod-total-sales">₹{salesData.total_sales.toFixed(2)}</p>
                  </div>
                  <div className="bg-green-50 rounded-xl p-4">
                    <p className="text-xs text-muted-foreground mb-1">Cash</p>
                    <p className="text-xl font-bold text-green-700" data-testid="eod-cash-total">₹{salesData.cash_total.toFixed(2)}</p>
                  </div>
                  <div className="bg-blue-50 rounded-xl p-4">
                    <p className="text-xs text-muted-foreground mb-1">UPI</p>
                    <p className="text-xl font-bold text-blue-700" data-testid="eod-upi-total">₹{salesData.upi_total.toFixed(2)}</p>
                  </div>
                  <div className="bg-purple-50 rounded-xl p-4">
                    <p className="text-xs text-muted-foreground mb-1">Card</p>
                    <p className="text-xl font-bold text-purple-700" data-testid="eod-card-total">₹{salesData.card_total.toFixed(2)}</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Orders Controls */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold text-foreground">Orders</h2>
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-40" data-testid="status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Orders</SelectItem>
                <SelectItem value="received">Received</SelectItem>
                <SelectItem value="preparing">Preparing</SelectItem>
                <SelectItem value="delivered">Delivered</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Badge variant="outline">{filteredOrders.length} orders</Badge>
        </div>

        {/* Orders Table */}
        {filteredOrders.length === 0 ? (
          <div className="text-center py-20 bg-card rounded-xl border border-border/50">
            <Package className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <p className="text-lg text-muted-foreground">No orders found</p>
          </div>
        ) : (
          <div className="bg-card rounded-xl border border-border/50 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="orders-table">
                <thead>
                  <tr className="border-b border-border bg-muted/30">
                    <th className="text-left px-4 py-3 font-semibold text-muted-foreground">Order</th>
                    <th className="text-left px-4 py-3 font-semibold text-muted-foreground">Room</th>
                    <th className="text-left px-4 py-3 font-semibold text-muted-foreground">Patient</th>
                    <th className="text-left px-4 py-3 font-semibold text-muted-foreground">Brand</th>
                    <th className="text-left px-4 py-3 font-semibold text-muted-foreground">Items</th>
                    <th className="text-left px-4 py-3 font-semibold text-muted-foreground">Payment</th>
                    <th className="text-right px-4 py-3 font-semibold text-muted-foreground">Amount</th>
                    <th className="text-center px-4 py-3 font-semibold text-muted-foreground">Status</th>
                    <th className="text-center px-4 py-3 font-semibold text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrders.map((order) => {
                    const actualPay = order.actual_payment_mode || order.payment_mode;
                    const paymentChanged = order.actual_payment_mode && order.actual_payment_mode !== order.payment_mode;
                    const isUnconfirmed = order.room_confirmed === false;
                    return (
                      <tr key={order.order_id}
                        className={`border-b border-border/50 hover:bg-muted/10 transition-colors ${isUnconfirmed ? 'bg-amber-50/50' : ''}`}
                        data-testid={`order-${order.order_id}`}>
                        {/* Order ID + Time */}
                        <td className="px-4 py-3">
                          <p className="font-semibold text-foreground text-xs" data-testid="order-id-display">#{order.order_id}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {format(new Date(order.created_at), "dd MMM, hh:mm a")}
                          </p>
                        </td>
                        {/* Room */}
                        <td className="px-4 py-3">
                          <span className="text-lg font-bold text-primary" data-testid="order-room-number">{order.room_number}</span>
                          {order.source === "kiosk" && (
                            <span className="block text-[10px] text-muted-foreground">Kiosk</span>
                          )}
                          {isUnconfirmed && (
                            <span className="inline-block mt-1 text-[10px] font-bold text-amber-700 bg-amber-100 border border-amber-300 rounded px-1.5 py-0.5"
                              data-testid={`unconfirmed-badge-${order.order_id}`}>
                              ⚠ ROOM TO BE CONFIRMED
                            </span>
                          )}
                        </td>
                        {/* Patient */}
                        <td className="px-4 py-3">
                          <p className="font-medium text-foreground" data-testid="order-patient-name">{order.patient_name || "—"}</p>
                          <p className="text-xs text-muted-foreground" data-testid="order-phone-number">{order.phone_number || ""}</p>
                        </td>
                        {/* Brand */}
                        <td className="px-4 py-3">
                          <Badge variant="outline" className="text-xs" data-testid="order-brand">{order.brand_name}</Badge>
                        </td>
                        {/* Items */}
                        <td className="px-4 py-3 max-w-[200px]">
                          <div className="space-y-0.5">
                            {order.items.map((item, idx) => (
                              <p key={idx} className="text-xs text-foreground truncate" data-testid={`order-item-${idx}`}>
                                {item.name} <span className="text-muted-foreground">x{item.quantity}</span>
                              </p>
                            ))}
                          </div>
                          {order.special_instructions && (
                            <p className="text-xs text-orange-600 mt-1 italic truncate" title={order.special_instructions}>
                              Note: {order.special_instructions}
                            </p>
                          )}
                        </td>
                        {/* Payment */}
                        <td className="px-4 py-3">
                          <div className="space-y-1">
                            <Select
                              value={actualPay}
                              onValueChange={(val) => updatePaymentMode(order.order_id, val)}
                              disabled={updatingPayment === order.order_id}
                            >
                              <SelectTrigger className="w-24 h-8 text-xs" data-testid={`payment-select-${order.order_id}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="cash">Cash</SelectItem>
                                <SelectItem value="upi">UPI</SelectItem>
                                <SelectItem value="card">Card</SelectItem>
                              </SelectContent>
                            </Select>
                            {paymentChanged && (
                              <p className="text-[10px] text-muted-foreground">
                                was: <span className="capitalize line-through">{order.payment_mode}</span>
                              </p>
                            )}
                          </div>
                        </td>
                        {/* Amount */}
                        <td className="px-4 py-3 text-right">
                          <span className="font-bold text-accent" data-testid="order-total">₹{order.total_amount.toFixed(2)}</span>
                        </td>
                        {/* Status */}
                        <td className="px-4 py-3 text-center">
                          <Badge className={`rounded-full border text-xs ${statusColors[order.status]}`}>
                            {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                          </Badge>
                        </td>
                        {/* Actions */}
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-center gap-2 flex-wrap">
                            <Button variant="ghost" size="icon" className="h-8 w-8"
                              onClick={() => handlePrintOrder(order)}
                              data-testid={`print-order-${order.order_id}`}
                              title="Print order">
                              <Printer className="h-4 w-4" />
                            </Button>
                            {isUnconfirmed && (
                              <Button size="sm"
                                className="h-8 text-xs bg-amber-600 hover:bg-amber-700 text-white"
                                onClick={() => confirmRoom(order.order_id)}
                                data-testid={`confirm-room-${order.order_id}`}>
                                Confirm Room
                              </Button>
                            )}
                            {order.status === "received" && (
                              <Button size="sm"
                                className="h-8 text-xs bg-yellow-600 hover:bg-yellow-700 text-white"
                                onClick={() => updateOrderStatus(order.order_id, "preparing")}
                                data-testid={`mark-preparing-${order.order_id}`}>
                                Preparing
                              </Button>
                            )}
                            {order.status === "preparing" && (
                              <Button size="sm"
                                className="h-8 text-xs bg-green-600 hover:bg-green-700 text-white"
                                onClick={() => updateOrderStatus(order.order_id, "delivered")}
                                data-testid={`mark-delivered-${order.order_id}`}>
                                Delivered
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default KitchenDashboard;
